import { useCallback, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { ehandoverAPI } from '../../../../apis';
import { getRealBash64Str, isImageFile } from '../../../../utils';
import useUploadFile, { ACCEPT_FILE_TYPES, convertFile } from '../../../../utils/hooks/useUploadFile';

const DEFAULT_MAX_ATTACHMENT_COUNT = 10;
const UPOLOAD_FILE_MAX_SIZE = 10 * 1024 * 1024;

const defaultUploadFile = async ({ content, name, taskId, setUploadDraftTaskId, uploadTaskIdRef }) => {
  const params = {
    content,
    name,
  };
  if (taskId ?? uploadTaskIdRef.current) {
    params.taskId = taskId ?? uploadTaskIdRef.current;
  }
  const { result } = await ehandoverAPI.uploadFileAndCreateTask(params, { isHideLoading: true });
  if (result?.taskId) {
    setUploadDraftTaskId(result.taskId);
  }
  return {
    taskId: result?.taskId ?? null,
    attachmentId: result?.attachmentId ?? null,
  };
};

const defaultRemoveFile = async ({ taskId, attachmentId }) => {
  await ehandoverAPI.removeTaskFile({
    taskId,
    attachmentId,
  });
};

const createAsyncQueue = () => {
  const queue = [];
  let isProcessing = false;

  const processNext = async () => {
    // Stop if already running or if the queue is empty
    if (isProcessing || queue.length === 0) {return;}

    isProcessing = true;
    const { asyncTask, resolve, reject } = queue.shift();
    if (typeof asyncTask === 'function') {
      try {
        const result = await asyncTask();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        isProcessing = false;
        processNext(); // Move to the next task in the queue
      }
    }else{
      isProcessing = false;
      processNext(); // Move to the next task in the queue
    }
   
  };

  // Return a function that accepts an async task and returns a Promise
  return {
    addToQueue:  (asyncTask) => {
      const taskPromise = new Promise((resolve, reject) => {
        queue.push({ asyncTask, resolve, reject });
        processNext();
      });
      return taskPromise;
    }
  }
};

function cloneFileList(fileList) {
  const dataTransfer = new DataTransfer();
  
  for (let i = 0; i < fileList.length; i++) {
    const file = fileList[i];
    // Create an independent copy of each underlying File object
    const clonedFile = new File([file], file.name, {
      type: file.type,
      lastModified: file.lastModified
    });
    dataTransfer.items.add(clonedFile);
  }
  
  return dataTransfer.files; // Returns a brand new FileList instance
}

const useTaskFileUpload = ({
  maxAttachmentCount = DEFAULT_MAX_ATTACHMENT_COUNT,
  uploadFile = defaultUploadFile,
  removeFile = defaultRemoveFile,
} = {}) => {
  const { imgList, setImgList, fileList, setFileList } = useUploadFile();
  const uploadTaskIdRef = useRef(null);
  const [uploadTaskId, setUploadTaskId] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [warnFile, setWarnFile] = useState([]);
  const [fileTypeWarn, setFileTypeWarn] = useState([]);
  const [attachmentLimitWarnOpen, setAttachmentLimitWarnOpen] = useState(false);
  const [attachmentUploadingWarnOpen, setAttachmentUploadingWarnOpen] = useState(false);
  const enqueue = useRef(createAsyncQueue());

  const setUploadDraftTaskId = useCallback((taskId = null) => {
    uploadTaskIdRef.current = taskId;
    setUploadTaskId(taskId);
  }, []);

  const isUploadingAttachment = useMemo(() => {
    return [...imgList, ...fileList].some((item) => item?.uploading);
  }, [imgList, fileList]);

  const handleUploadTaskFile = useCallback(
    async (content, name) => {
      const result = await uploadFile({
        content,
        name,
        taskId: uploadTaskIdRef.current,
        setUploadDraftTaskId,
        uploadTaskIdRef,
      });
      if (result?.taskId) {
        setUploadDraftTaskId(result.taskId);
      }
      return result?.attachmentId ?? null;
    },
    [setUploadDraftTaskId, uploadFile]
  );

  const updateSelectedFileUploadState = useCallback(
    (type, clientId, updater) => {
      const setData = type === 'photo' ? setImgList : setFileList;
      setData((prev) =>
        prev.map((item) => {
          if (item.clientId !== clientId) {
            return item;
          }
          return updater(item);
        })
      );
    },
    [setImgList, setFileList]
  );

  const removeSelectedFileByClientId = useCallback(
    (type, clientId) => {
      const setData = type === 'photo' ? setImgList : setFileList;
      setData((prev) => prev.filter((item) => item.clientId !== clientId));
    },
    [setImgList, setFileList]
  );

  const innerHandleSelectFile = useCallback(
    async (uploadFiles) => {
      const currentAttachmentCount = (imgList?.length ?? 0) + (fileList?.length ?? 0);
      const remainingCount = maxAttachmentCount - currentAttachmentCount;

      if (remainingCount <= 0) {
        setAttachmentLimitWarnOpen(true);
        return;
      }

      const validUploadFiles = Array.from(uploadFiles ?? []).slice(0, remainingCount);
      if ((uploadFiles?.length ?? 0) > remainingCount) {
        setAttachmentLimitWarnOpen(true);
      }

      // 先插入占位附件，再逐个异步上传回填，这样每个文件都能独立显示 loading。
      for (let index = 0; index < validUploadFiles.length; index++) {
        const currentFile = validUploadFiles[index];
        const { name: fileName, size: fileSize } = currentFile;
        const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();

        if (fileSize >= UPOLOAD_FILE_MAX_SIZE) {
          setWarnFile((prevState) => [...prevState, fileName]);
          setAlertOpen(true);
          continue;
        }
        if (!ACCEPT_FILE_TYPES.includes(`.${fileExtension}`)) {
          setFileTypeWarn((prevState) => [...prevState, fileName]);
          setAlertOpen(true);
          continue;
        }

        const isImg = isImageFile(fileName);
        const previewUrl = isImg ? URL.createObjectURL(currentFile) : '';
        const clientId = _.uniqueId('ehandover_upload_');
        const fileObj = {
          clientId,
          name: fileName,
          base64: [],
          previewUrl,
          uploading: true,
        };

        if (isImg) {
          setImgList((prev) => [...prev, fileObj]);
        } else {
          setFileList((prev) => [...prev, fileObj]);
        }

        try {
          const result = await convertFile(currentFile);
          let tmpbase64 = getRealBash64Str(result);
          if (tmpbase64?.length > 0) {
            tmpbase64 = [tmpbase64];
          }
          const attachmentId = await handleUploadTaskFile(tmpbase64?.[0], fileName);
          updateSelectedFileUploadState(isImg ? 'photo' : 'file', clientId, (item) => ({
            ...item,
            id: attachmentId,
            base64: tmpbase64 ?? [],
            uploading: false,
          }));
        } catch (error) {
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          removeSelectedFileByClientId(isImg ? 'photo' : 'file', clientId);
        }
      }
    },
    [
      fileList,
      handleUploadTaskFile,
      imgList,
      maxAttachmentCount,
      removeSelectedFileByClientId,
      setFileList,
      setImgList,
      updateSelectedFileUploadState,
    ]
  );

  const handleSelectFile = useCallback(
    async (uploadFiles) => {
      const cloneFiles = cloneFileList(uploadFiles);
      await enqueue.current.addToQueue(async () => {await innerHandleSelectFile(cloneFiles)});
    },
    [
      innerHandleSelectFile
    ]
  );

  const handleDragDropUpload = useCallback(
    async (files) => {
      await handleSelectFile(files);
    },
    [handleSelectFile]
  );

  const removeSelectedFile = useCallback(
    async (fileType, item, index) => {
      if (uploadTaskIdRef.current && item?.id) {
        await removeFile({
          taskId: uploadTaskIdRef.current,
          attachmentId: item.id,
        });
      }
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
      if (fileType === 'photo') {
        setImgList((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
        return;
      }
      setFileList((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
    },
    [removeFile, setImgList, setFileList]
  );

  const handleDeletePhoto = useCallback(
    async (item, index) => {
      await removeSelectedFile('photo', item, index);
    },
    [removeSelectedFile]
  );

  const handleDeleteFile = useCallback(
    async (item, index) => {
      await removeSelectedFile('file', item, index);
    },
    [removeSelectedFile]
  );

  return {
    imgList,
    setImgList,
    fileList,
    setFileList,
    uploadTaskId,
    uploadTaskIdRef,
    setUploadDraftTaskId,
    alertOpen,
    setAlertOpen,
    warnFile,
    setWarnFile,
    fileTypeWarn,
    setFileTypeWarn,
    attachmentLimitWarnOpen,
    setAttachmentLimitWarnOpen,
    attachmentUploadingWarnOpen,
    setAttachmentUploadingWarnOpen,
    isUploadingAttachment,
    handleSelectFile,
    handleDragDropUpload,
    handleDeletePhoto,
    handleDeleteFile,
  };
};

export default useTaskFileUpload;
